/**
 * Per-route SEO meta data for server-side rendering.
 *
 * The server uses this to inject the correct <title>, description,
 * canonical URL, OG tags, and hreflang into the HTML before sending
 * it to the browser / crawler.
 */

import { translations } from '../translations';
import type { Language } from '../types';
import { DEFAULT_LANGUAGE, SUPPORTED_LANGUAGES, LANGUAGE_CONFIG } from '../types/translations';

const SITE_URL = 'https://ronnieprojects.com';

export interface RouteMeta {
  title: string;
  description: string;
  canonical: string;
  lang: Language;
  dir: 'ltr' | 'rtl';
  ogLocale: string;
  robots?: string;
  /** Page-specific SEO keywords; falls back to the default in index.html when absent */
  keywords?: string;
  /** Per-page Open Graph image URL (absolute); falls back to the static og-image.png when absent */
  ogImage?: string;
  /** Alternate language URLs for hreflang tags */
  alternates: { lang: string; href: string }[];
}

/** Parse URL into language and route path (without language prefix). */
function parseLangAndPath(url: string): { lang: Language; routePath: string } {
  const pathname = (url.split('?')[0] ?? '/').split('#')[0] ?? '/';
  const segments = pathname.split('/').filter(Boolean);

  let lang: Language = DEFAULT_LANGUAGE;
  let routePath = pathname;

  if (segments.length > 0 && SUPPORTED_LANGUAGES.includes(segments[0] as Language)) {
    lang = segments[0] as Language;
    routePath = '/' + segments.slice(1).join('/');
  }

  // Normalise: remove trailing slash (except root)
  if (routePath !== '/' && routePath.endsWith('/')) {
    routePath = routePath.slice(0, -1);
  }
  if (!routePath.startsWith('/')) {
    routePath = '/' + routePath;
  }

  return { lang, routePath };
}

/** Build hreflang alternates for a given base path. */
function buildAlternates(basePath: string): { lang: string; href: string }[] {
  const alts: { lang: string; href: string }[] = [];
  const suffix = basePath === '/' ? '' : basePath;

  // English (default) — no prefix
  alts.push({ lang: 'en', href: `${SITE_URL}${suffix || '/'}` });

  // Other languages — with prefix
  for (const code of SUPPORTED_LANGUAGES) {
    alts.push({ lang: code, href: `${SITE_URL}/${code}${suffix}` });
  }

  // x-default points to the default language
  alts.push({ lang: 'x-default', href: `${SITE_URL}${suffix || '/'}` });

  return alts;
}

/**
 * Return SEO metadata for a given request URL.
 * Called by the server before rendering so the correct tags are injected
 * into the HTML template.
 */
export function getRouteMeta(url: string): RouteMeta {
  const { lang, routePath } = parseLangAndPath(url);
  const t = translations[lang];
  const isHe = lang === 'he';
  const prefix = isHe ? '/he' : '';

  const base: Pick<RouteMeta, 'lang' | 'dir' | 'ogLocale'> = {
    lang,
    dir: LANGUAGE_CONFIG[lang].isRTL ? 'rtl' : 'ltr',
    ogLocale: isHe ? 'he_IL' : 'en_US',
  };

  const seo = t.seo;

  switch (routePath) {
    case '/':
      return {
        ...base,
        title: seo.home.title,
        description: seo.home.description,
        canonical: `${SITE_URL}${prefix || '/'}`,
        alternates: buildAlternates('/'),
      };

    case '/about':
      return {
        ...base,
        title: seo.about.title,
        description: seo.about.description,
        canonical: `${SITE_URL}${prefix}/about`,
        alternates: buildAlternates('/about'),
      };

    case '/ai-agents':
      return {
        ...base,
        title: seo.aiAgents.title,
        description: seo.aiAgents.description,
        canonical: `${SITE_URL}${prefix}/ai-agents`,
        alternates: buildAlternates('/ai-agents'),
      };

    case '/integration-services':
      return {
        ...base,
        title: seo.integrationServices.title,
        description: seo.integrationServices.description,
        canonical: `${SITE_URL}${prefix}/integration-services`,
        alternates: buildAlternates('/integration-services'),
      };

    case '/custom-development':
      return {
        ...base,
        title: seo.customDevelopment.title,
        description: seo.customDevelopment.description,
        canonical: `${SITE_URL}${prefix}/custom-development`,
        alternates: buildAlternates('/custom-development'),
      };

    case '/contact':
      return {
        ...base,
        title: seo.contact.title,
        description: seo.contact.description,
        canonical: `${SITE_URL}${prefix}/contact`,
        alternates: buildAlternates('/contact'),
      };

    case '/case-studies':
      return {
        ...base,
        title: seo.caseStudies.title,
        description: seo.caseStudies.description,
        canonical: `${SITE_URL}${prefix}/case-studies`,
        alternates: buildAlternates('/case-studies'),
      };

    case '/articles':
      return {
        ...base,
        title: seo.articles.title,
        description: seo.articles.description,
        canonical: `${SITE_URL}${prefix}/articles`,
        alternates: buildAlternates('/articles'),
      };

    case '/resources':
      return {
        ...base,
        title: seo.resources.title,
        description: seo.resources.description,
        canonical: `${SITE_URL}${prefix}/resources`,
        alternates: buildAlternates('/resources'),
      };

    case '/privacy':
      return {
        ...base,
        title: seo.privacy.title,
        description: seo.privacy.description,
        canonical: `${SITE_URL}${prefix}/privacy`,
        alternates: buildAlternates('/privacy'),
        robots: 'noindex, follow',
      };

    case '/terms':
      return {
        ...base,
        title: seo.terms.title,
        description: seo.terms.description,
        canonical: `${SITE_URL}${prefix}/terms`,
        alternates: buildAlternates('/terms'),
        robots: 'noindex, follow',
      };

    case '/accessibility':
      return {
        ...base,
        title: seo.accessibility.title,
        description: seo.accessibility.description,
        canonical: `${SITE_URL}${prefix}/accessibility`,
        alternates: buildAlternates('/accessibility'),
        robots: 'noindex, follow',
      };

    case '/tech-partner':
      // Static, Hebrew-only landing page. Only serve real meta for the Hebrew
      // URL; the (non-existent) English URL falls through to 404 meta below.
      if (isHe) {
        return {
          ...base,
          title: 'שותף טכנולוגי לעסק שלך | Ronnie Projects',
          description:
            'שותף טכנולוגי בכיר לעסק שלך — מנהל טכנולוגיה במיקור חוץ לחברות עם ERP (Priority/SAP), חנות אונליין ותוכניות צמיחה, בלי משכורת של CTO. אסטרטגיה, פיקוח על ספקים והחלטות נכונות.',
          canonical: `${SITE_URL}/he/tech-partner`,
          alternates: [],
        };
      }
      break;

    default:
      break;
  }

  // Case study detail pages
  if (routePath.startsWith('/case-studies/')) {
    const slug = routePath.replace('/case-studies/', '');
    return {
      ...base,
      title: seo.caseStudyDetail.title.replace('{slug}', slug),
      description: seo.caseStudyDetail.description.replace('{slug}', slug),
      canonical: `${SITE_URL}${prefix}/case-studies/${slug}`,
      alternates: buildAlternates(`/case-studies/${slug}`),
    };
  }

  // Article detail pages
  if (routePath.startsWith('/articles/')) {
    const slug = routePath.replace('/articles/', '');
    return {
      ...base,
      title: seo.articleDetail.title.replace('{slug}', slug),
      description: seo.articleDetail.description.replace('{slug}', slug),
      canonical: `${SITE_URL}${prefix}/articles/${slug}`,
      alternates: buildAlternates(`/articles/${slug}`),
    };
  }

  // Resource detail pages
  if (routePath.startsWith('/resources/')) {
    const slug = routePath.replace('/resources/', '');
    return {
      ...base,
      title: seo.resourceDetail.title.replace('{slug}', slug),
      description: seo.resourceDetail.description.replace('{slug}', slug),
      canonical: `${SITE_URL}${prefix}/resources/${slug}`,
      alternates: buildAlternates(`/resources/${slug}`),
    };
  }

  // Admin pages — no indexing
  if (routePath.startsWith('/admin')) {
    return {
      ...base,
      lang: 'en' as Language,
      dir: 'ltr',
      ogLocale: 'en_US',
      title: `Admin | ${seo.siteName}`,
      description: 'Admin panel',
      canonical: `${SITE_URL}/admin`,
      alternates: [],
      robots: 'noindex, nofollow',
    };
  }

  // 404 — unknown route
  return {
    ...base,
    title: seo.notFoundPage.title,
    description: seo.notFoundPage.description,
    canonical: `${SITE_URL}${prefix || '/'}`,
    alternates: [],
    robots: 'noindex, follow',
  };
}
